import React, { useState } from 'react';
import type { Npc, NpcAbility, NpcScaleBasis, NpcStatKey } from '../types';
import { API_URL } from '../utils/api';
import { PictogramPicker } from './PictogramPicker';
import { NpcMark } from './Pictogram';
import { fallbackNpcIcon, isKitIcon } from '../constants/pictograms';
import { mediaSrc } from '../utils/media';

const NPC_TYPES = ['Ork', 'Goblin', 'Drache', 'Untoter', 'Bandit', 'Bestie', 'Dämon', 'Konstrukt', 'Mensch'];

const emptyAbility = (): NpcAbility => ({ name: '', formula: 'damage', description: '' });

const PERCENT_MODES = ['level', 'player_count', 'combat_heroes', 'combat_foes'] as const;

const STATS: { key: NpcStatKey; label: string }[] = [
  { key: 'health', label: 'Leben' },
  { key: 'damage', label: 'Schaden' },
  { key: 'movement', label: 'Bewegung' },
];

const BASES: { key: NpcScaleBasis; label: string; fallback: number }[] = [
  { key: 'level', label: 'Level', fallback: 20 },
  { key: 'player_count', label: 'Anzahl Spieler', fallback: 15 },
  { key: 'combat_heroes', label: 'Helden im Kampf', fallback: 15 },
];

type RuleMap = Record<NpcStatKey, Partial<Record<NpcScaleBasis, number>>>;

const PREVIEW = { level: 3, player_count: 4, combat_heroes: 3, combat_foes: 3 };

function percentFromFormula(formula?: string | null) {
  const match = String(formula || '').trim().match(
    /^1\s*\+\s*([0-9]+(?:\.[0-9]+)?)\s*\*\s*\(\s*(?:level|player_count|hero_count|helden|foe_count|gegner)\s*-\s*1\s*\)$/,
  );
  if (!match) return null;
  return Math.round(parseFloat(match[1]) * 1000) / 10;
}

function blankRules(): RuleMap {
  return { health: {}, damage: {}, movement: {} };
}

function rulesFromNpc(initial?: Npc | null): RuleMap {
  const rules = blankRules();
  if (!initial) return rules;
  if (initial.scaling_mode === 'per_stat' && initial.stat_scaling) {
    STATS.forEach(stat => {
      rules[stat.key] = foldOpponents(initial.stat_scaling?.[stat.key] || {});
    });
    return rules;
  }
  if (initial.scaling_mode && (PERCENT_MODES as readonly string[]).includes(initial.scaling_mode)) {
    const basis = initial.scaling_mode as NpcScaleBasis;
    const percent = percentFromFormula(initial.scaling_formula) ?? (
      basis === 'combat_foes' ? 15 : BASES.find(item => item.key === basis)!.fallback
    );
    STATS.forEach(stat => {
      rules[stat.key] = foldOpponents({ [basis]: percent });
    });
  }
  return rules;
}

function foldOpponents(entry: Partial<Record<NpcScaleBasis, number>>) {
  const next = { ...entry };
  if (next.combat_foes != null && next.combat_heroes == null) {
    next.combat_heroes = next.combat_foes;
  }
  delete next.combat_foes;
  return next;
}

function factorFor(rules: Partial<Record<NpcScaleBasis, number>>) {
  return BASES.reduce((factor, basis) => {
    const percent = rules[basis.key];
    if (percent == null) return factor;
    return factor * (1 + (percent / 100) * Math.max(0, PREVIEW[basis.key] - 1));
  }, 1);
}

function scaledAmount(base: number, factor: number) {
  return Math.max(0, Math.round(base * factor));
}

function formatFactor(factor: number) {
  return factor.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface NpcModalProps {
  initial?: Npc | null;
  onClose: () => void;
  onSubmit: (payload: Partial<Npc>) => Promise<void>;
}

export function NpcModal({ initial, onClose, onSubmit }: NpcModalProps) {
  const editing = Boolean(initial);
  const [name, setName] = useState(initial?.name || '');
  const [npcType, setNpcType] = useState(initial?.npc_type || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [damage, setDamage] = useState(initial?.damage ?? 5);
  const [movement, setMovement] = useState(initial?.movement ?? 3);
  const [health, setHealth] = useState(initial?.health ?? 30);
  const [maxHealth, setMaxHealth] = useState(initial?.max_health ?? 30);
  const [icon, setIcon] = useState(
    isKitIcon(initial?.icon) ? initial!.icon as string : fallbackNpcIcon(initial?.npc_type),
  );
  const [iconUrl, setIconUrl] = useState(initial?.icon_url || '');
  const [useFormula, setUseFormula] = useState(initial?.scaling_mode === 'formula');
  const [scalingFormula, setScalingFormula] = useState(initial?.scaling_formula || '');
  const [rules, setRules] = useState<RuleMap>(() => rulesFromNpc(initial));
  const [abilities, setAbilities] = useState<NpcAbility[]>(
    initial?.abilities?.length ? initial.abilities.map(a => ({ ...a })) : []
  );
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const setBasis = (stat: NpcStatKey, basis: NpcScaleBasis, enabled: boolean) => {
    setRules(current => {
      const next = { ...current[stat] };
      if (enabled) {
        next[basis] = next[basis] ?? BASES.find(item => item.key === basis)!.fallback;
      } else {
        delete next[basis];
      }
      return { ...current, [stat]: next };
    });
  };

  const setPercent = (stat: NpcStatKey, basis: NpcScaleBasis, value: number) => {
    setRules(current => ({
      ...current,
      [stat]: { ...current[stat], [basis]: Math.max(0, Math.min(500, value)) },
    }));
  };

  const updateAbility = (index: number, patch: Partial<NpcAbility>) => {
    setAbilities(current => current.map((ability, i) => (i === index ? { ...ability, ...patch } : ability)));
  };

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setFormError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/upload/npc-icon`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Upload fehlgeschlagen.');
      }
      setIconUrl(data.url);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Icon konnte nicht hochgeladen werden.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError('Bitte gib einen Namen ein.');
      return;
    }
    if (useFormula && !scalingFormula.trim()) {
      setFormError('Bitte eine Skalierungsformel eingeben (level, player_count, hero_count, foe_count).');
      return;
    }
    const hasRules = STATS.some(stat => Object.keys(rules[stat.key]).length > 0);
    const cleanAbilities = abilities
      .map(ability => ({
        name: ability.name.trim(),
        formula: ability.formula.trim() || 'damage',
        description: (ability.description || '').trim(),
      }))
      .filter(ability => ability.name);
    setFormError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name: trimmed,
        npc_type: npcType.trim(),
        description: description.trim(),
        damage,
        movement,
        health,
        max_health: Math.max(maxHealth, health),
        icon,
        icon_url: iconUrl || null,
        scaling_mode: useFormula ? 'formula' : (hasRules ? 'per_stat' : 'none'),
        scaling_formula: useFormula ? (scalingFormula.trim() || null) : null,
        stat_scaling: useFormula || !hasRules ? null : rules,
        abilities: cleanAbilities,
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Gegner konnte nicht gespeichert werden.');
      setSubmitting(false);
    }
  };

  const previewBases: Record<NpcStatKey, number> = {
    health: Math.max(maxHealth, health),
    damage,
    movement,
  };
  const previewLines = STATS.flatMap(stat => {
    const chosen = BASES.filter(basis => rules[stat.key][basis.key] != null);
    if (!chosen.length) return [];
    const factor = factorFor(rules[stat.key]);
    const parts = chosen.map(basis => formatFactor(
      1 + ((rules[stat.key][basis.key] || 0) / 100) * Math.max(0, PREVIEW[basis.key] - 1),
    ));
    return [`${stat.label} ${previewBases[stat.key]} × ${parts.join(' × ')} = ${scaledAmount(previewBases[stat.key], factor)}`];
  });
  const previewText = previewLines.length
    ? `Beispiel Stufe 3, 4 Spieler, 3 Helden: ${previewLines.join(' · ')}`
    : 'Ohne Haken bleiben Schaden, Bewegung und Leben beim Grundwert.';

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-content game-create-modal npc-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="npc-modal-title"
      >
        <div className="modal-header">
          <h2 id="npc-modal-title">
            <NpcMark icon={icon} iconUrl={iconUrl} npcType={npcType} className="npc-modal-title-icon" />
            {editing ? 'Gegner bearbeiten' : 'Neuer Gegner'}
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Schließen">✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {formError && <p className="form-error" role="alert">{formError}</p>}
            <div className="form-group">
              <label htmlFor="npc-name">Name *</label>
              <input id="npc-name" value={name} onChange={e => setName(e.target.value)} required autoFocus />
            </div>
            <div className="form-group">
              <label htmlFor="npc-type">Typ / Kategorie</label>
              <input
                id="npc-type"
                list="npc-type-list"
                value={npcType}
                onChange={e => {
                  const nextType = e.target.value;
                  if (!iconUrl && icon === fallbackNpcIcon(npcType)) {
                    setIcon(fallbackNpcIcon(nextType));
                  }
                  setNpcType(nextType);
                }}
                placeholder="z.B. Ork"
              />
              <datalist id="npc-type-list">
                {NPC_TYPES.map(type => <option key={type} value={type} />)}
              </datalist>
            </div>
            <div className="form-group">
              <label htmlFor="npc-description">Beschreibung</label>
              <textarea
                id="npc-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div className="npc-stat-grid">
              <div className="form-group">
                <label htmlFor="npc-damage">Schaden</label>
                <input id="npc-damage" type="number" min={0} value={damage} onChange={e => setDamage(parseInt(e.target.value) || 0)} />
              </div>
              <div className="form-group">
                <label htmlFor="npc-movement">Bewegung</label>
                <input id="npc-movement" type="number" min={0} value={movement} onChange={e => setMovement(parseInt(e.target.value) || 0)} />
              </div>
              <div className="form-group">
                <label htmlFor="npc-health">Leben</label>
                <input id="npc-health" type="number" min={0} value={health} onChange={e => setHealth(parseInt(e.target.value) || 0)} />
              </div>
              <div className="form-group">
                <label htmlFor="npc-max-health">Max. Leben</label>
                <input id="npc-max-health" type="number" min={0} value={maxHealth} onChange={e => setMaxHealth(parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <div className="form-group">
              <span className="npc-field-label">Wappen</span>
              <PictogramPicker
                kind="npc"
                value={iconUrl ? '' : icon}
                onChange={chosen => {
                  setIcon(chosen.src);
                  setIconUrl('');
                }}
              />
              <label className="npc-upload-label">
                {uploading ? 'Lade hoch…' : 'Eigenes Bild (png/jpg/webp, max. 2 MB)'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  hidden
                  onChange={e => void handleUpload(e.target.files?.[0])}
                />
              </label>
              {iconUrl ? (
                <img className="npc-upload-preview" src={mediaSrc(iconUrl)} alt="" />
              ) : null}
            </div>
            <div className="form-group">
              <span className="npc-field-label">Skalierung je Wert</span>
              <p className="section-info">
                Jeder Wert kann Level, Spielerzahl oder die Helden im Kampf folgen. Die Helden sind die Gegner des Monsters. Spieler auf der Seite des Monsters sind Verbündete und zählen nicht. Die erste Stufe, der erste Spieler und der erste Held bleiben der Grundwert.
              </p>
              {!useFormula && STATS.map(stat => (
                <fieldset key={stat.key} className="npc-scale-block">
                  <h3>{stat.label} skaliert nach</h3>
                  {BASES.map(basis => {
                    const percent = rules[stat.key][basis.key];
                    const active = percent != null;
                    return (
                      <div key={basis.key} className="npc-scale-row">
                        <input
                          id={`scale-${stat.key}-${basis.key}`}
                          type="checkbox"
                          aria-label={`${stat.label} nach ${basis.label}`}
                          checked={active}
                          onChange={event => setBasis(stat.key, basis.key, event.target.checked)}
                        />
                        <label htmlFor={`scale-${stat.key}-${basis.key}`}>{basis.label}</label>
                        <input
                          type="number"
                          min={0}
                          max={500}
                          step={1}
                          aria-label={`${stat.label} Prozent ${basis.label}`}
                          disabled={!active}
                          value={active ? percent : ''}
                          onChange={event => setPercent(stat.key, basis.key, parseFloat(event.target.value) || 0)}
                        />
                      </div>
                    );
                  })}
                </fieldset>
              ))}
              {!useFormula && <p className="npc-scale-preview">{previewText}</p>}
              <label className="npc-scale-row">
                <input
                  type="checkbox"
                  aria-label="Eigene Formel für alle Werte"
                  checked={useFormula}
                  onChange={event => setUseFormula(event.target.checked)}
                />
                <span>Eigene Formel für alle Werte</span>
              </label>
            </div>
            {useFormula && (
              <div className="form-group">
                <label htmlFor="npc-scaling-formula">Skalierungsformel *</label>
                <input
                  id="npc-scaling-formula"
                  value={scalingFormula}
                  onChange={e => setScalingFormula(e.target.value)}
                  placeholder="z.B. 1 + 0.2 * (level - 1)"
                />
                <p className="section-info">Variablen: level, player_count, hero_count, foe_count. Der Faktor gilt für Schaden, Bewegung, Leben und Fähigkeiten.</p>
              </div>
            )}
            <div className="form-group">
              <span className="npc-field-label">Fähigkeiten</span>
              {abilities.map((ability, index) => (
                <div key={index} className="npc-ability-row">
                  <input
                    aria-label={`Fähigkeit ${index + 1} Name`}
                    placeholder="Name"
                    value={ability.name}
                    onChange={e => updateAbility(index, { name: e.target.value })}
                  />
                  <input
                    aria-label={`Fähigkeit ${index + 1} Formel`}
                    placeholder="Formel (z.B. damage)"
                    value={ability.formula}
                    onChange={e => updateAbility(index, { formula: e.target.value })}
                  />
                  <input
                    aria-label={`Fähigkeit ${index + 1} Beschreibung`}
                    placeholder="Beschreibung"
                    value={ability.description || ''}
                    onChange={e => updateAbility(index, { description: e.target.value })}
                  />
                  <button type="button" className="delete-button" onClick={() => setAbilities(current => current.filter((_, i) => i !== index))}>
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="medieval-button" onClick={() => setAbilities(current => [...current, emptyAbility()])}>
                + Fähigkeit
              </button>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="medieval-button cancel-btn" onClick={onClose} disabled={submitting}>
              Abbrechen
            </button>
            <button type="submit" className="medieval-button create-btn" disabled={submitting}>
              {submitting ? 'Speichere…' : (editing ? 'Speichern' : 'Gegner anlegen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
